/**
 * ServiceRequestsListWidget — date-grouped list of recent requests.
 * Time is the entry_date's HH:MM. No status color since request_parent.status
 * is free-text varchar and the dashboard catalog doesn't hydrate it (yet).
 */

import * as React from "react";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "react-query";
import { DateGroupedList, RefreshButton, ViewAllLink, WidgetCard } from "components/dashboard";
import { useServiceRequestsList, formatQueryError } from "../hooks";
import type { LayoutItem, WidgetConfig } from "../types";

interface Props {
  widget: WidgetConfig;
  layout: LayoutItem;
}

export function ServiceRequestsListWidget({ widget, layout }: Props) {
  const { data, isLoading, error } = useServiceRequestsList();
  const history = useHistory();
  const queryClient = useQueryClient();

  const groups = (data?.groups ?? []).map((g) => ({
    date: g.date,
    count: g.count,
    onViewMore: () => history.push("/requests"),
    items: g.items.map((item) => ({
      id: item.id,
      label: item.label,
      subLabel: item.subLabel,
      time: item.time,
      onClick: () => history.push(`/requests/${item.id}`),
    })),
  }));

  return (
    <WidgetCard
      title={widget.title}
      w={widget.defaultSize.w}
      h={widget.defaultSize.h}
      x={layout.x}
      y={layout.y}
      loading={isLoading}
      error={error ? formatQueryError(error) : null}
      headerRight={
        <RefreshButton
          onClick={() => queryClient.invalidateQueries(["dashboard", "service-requests-list"])}
        />
      }
      footer={
        <ViewAllLink label="View all requests" onClick={() => history.push("/requests")} />
      }
    >
      <DateGroupedList
        groups={groups}
        columnHeaders={{ left: "Date", right: "Requests Count" }}
        emptyMessage="No recent service requests"
      />
    </WidgetCard>
  );
}
